import React, { useCallback, useRef, useMemo, ReactNode, MouseEvent } from 'react';
import styled from 'styled-components/macro';
import { AddIcon } from 'assets/icons';
import Dropdown, { PopoverPlacement, IOption } from 'shared/components/Dropdown';
import Tooltip from 'shared/components/Tooltip';
import { IAvatarGroupSelectOption } from './interfaces';
import { AvatarSize } from '@televet/kibble-ui/build/components/Avatar';
import { ClinicUserAvatarFragment } from 'shared/types/graphql';
import { AvatarGroup, ClinicAvatar, PetParentAvatar } from 'shared/components/Avatars';

interface IAvatarGroupSelectProps {
  options: IAvatarGroupSelectOption[];
  popoverPlacement?: PopoverPlacement;
  avatarSize?: AvatarSize;
  addButtonTooltip?: ReactNode;
  onToggle?: (e: MouseEvent, option: IAvatarGroupSelectOption) => void;
  displayedAvatars?: IAvatarGroupSelectOption[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setDisabled?: (data: any) => boolean;
  disabledMessage?: string;
  isAddingAvatar?: boolean;
  onAvatarClick?: (e: MouseEvent, option?: IOption) => void;
  isMultiSelect?: boolean;
  showRemoveButton?: boolean;
  closeOnClick?: boolean;
  hasPermission?: boolean;
  isLoadingOptions?: boolean;
  hideDropdownOption?: boolean;
  renderDropdownActions?: () => JSX.Element | null;
  onLoadOptions?: () => void;
  max?: number;
}

export const AvatarGroupSelect = ({
  options = [],
  popoverPlacement = PopoverPlacement.Auto,
  avatarSize = 'md',
  addButtonTooltip = '',
  onToggle,
  displayedAvatars,
  setDisabled,
  disabledMessage,
  isAddingAvatar = true,
  onAvatarClick,
  isMultiSelect = true,
  closeOnClick = true,
  showRemoveButton = true,
  hasPermission = true,
  isLoadingOptions = false,
  hideDropdownOption = false,
  renderDropdownActions,
  onLoadOptions,
  max = 3,
}: IAvatarGroupSelectProps): JSX.Element => {
  if (!options && !onLoadOptions) {
    throw new Error('One of `options` or `onLoadOptions` must be provided to AvatarGroupSelect');
  }

  const addAvatarButtonRef = useRef<HTMLButtonElement | null>(null);

  const handleAvatarClick = useCallback(
    (e: MouseEvent, option?: IOption) => {
      if (onAvatarClick) onAvatarClick(e, option);
      else addAvatarButtonRef?.current?.click();
    },
    [addAvatarButtonRef, onAvatarClick],
  );

  const displayedOptions = useMemo(
    () => (displayedAvatars ? displayedAvatars : options.filter((o) => !!o.isSelected)),
    [options, displayedAvatars],
  );

  const renderAddAvatarButton = (): JSX.Element => {
    const button = (
      <AddAvatarButton ref={addAvatarButtonRef} size={avatarSize}>
        <StyledAddIcon></StyledAddIcon>
      </AddAvatarButton>
    );
    if (!addButtonTooltip) {
      return button;
    }
    return <Tooltip content={addButtonTooltip}>{button}</Tooltip>;
  };

  const sortedOptions = useMemo(() => {
    return options.sort((a, b) => {
      if (a?.value?.firstName && a?.value?.lastName) {
        return a.value.firstName.localeCompare(b.value?.firstName);
      }
      return -1;
    });
  }, [options]);

  return (
    <AvatarGroup>
      {displayedOptions.length > max ? (
        <Dropdown
          options={options}
          placement={popoverPlacement}
          isMultiSelect={isMultiSelect}
          closeOnClick={closeOnClick}
          onSelect={onToggle}
          setDisabled={setDisabled}
          disabledMessage={disabledMessage}
          hideDropdownOption={!hasPermission}
        >
          <span>
            <Tooltip
              content={displayedOptions
                .map((o) => o.text)
                .sort((a, b) => a.localeCompare(b))
                .join(', ')}
              delay={300}
            >
              <AvatarCount
                onClick={(e: MouseEvent): void => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                size={avatarSize}
              >
                {displayedOptions.length}
              </AvatarCount>
            </Tooltip>
          </span>
        </Dropdown>
      ) : (
        displayedOptions.map((option, index) => {
          const { id, clinicPetParent, firstName, lastName, photoUrl, displayName } = option.value;
          const clinicMemberData: ClinicUserAvatarFragment = {
            firstName,
            lastName: lastName || '',
            profilePic: photoUrl,
            id,
            displayName: displayName || '',
          };

          if (clinicPetParent) {
            return (
              <PetParentAvatar
                key={`${id}-${index}`}
                clinicPetParent={clinicPetParent}
                size={avatarSize}
                isGrouped
                index={index}
                showRemoveButton={showRemoveButton}
                onRemove={(e: MouseEvent): void => onToggle?.(e, option)}
              />
            );
          }
          if (!hasPermission) {
            return <ClinicAvatar key={`${id}-${index}`} clinicMember={clinicMemberData} size={avatarSize} />;
          }

          return (
            <ClinicAvatar
              key={`${id}-${index}`}
              clinicMember={clinicMemberData}
              size={avatarSize}
              showRemoveButton={showRemoveButton}
              isGrouped
              index={index}
              onRemove={(e: MouseEvent): void => onToggle?.(e, option)}
              onClick={(e: MouseEvent): void => handleAvatarClick(e, option)}
            />
          );
        })
      )}
      {isAddingAvatar && hasPermission && (
        <Dropdown
          options={hideDropdownOption ? [] : sortedOptions}
          placement={popoverPlacement}
          isLazy={true}
          isMultiSelect={isMultiSelect}
          closeOnClick={closeOnClick}
          onSelect={onToggle}
          setDisabled={setDisabled}
          hideDropdownOption={hideDropdownOption}
          disabledMessage={disabledMessage}
          loadingOptions={isLoadingOptions}
          renderDropdownActions={renderDropdownActions}
          onLoadOptions={(): void => {
            if (typeof onLoadOptions === 'function') {
              onLoadOptions();
            }
          }}
        >
          <AddAvatarButtonContainer>{renderAddAvatarButton()}</AddAvatarButtonContainer>
        </Dropdown>
      )}
    </AvatarGroup>
  );
};

const StyledAddIcon = styled(AddIcon)`
  transition: all 0.1s ease-in-out;

  & * {
    fill: var(--chakra-colors-text-default);
  }
`;

const AddAvatarButtonContainer = styled.span`
  &:hover {
    z-index: 100;
  }

  &:not(:first-child) {
    margin-left: -0.4em;
  }
`;
interface IAddAvatarButtonProps {
  size?: AvatarSize;
}

const AddAvatarButton = styled.button<IAddAvatarButtonProps>`
  height: ${({ size }): string => getAvatarSize(size)};
  width: ${({ size }): string => getAvatarSize(size)};
  border-radius: ${({ size }): string => getAvatarSize(size)};
  border: 1px dashed var(--chakra-colors-text-default);
  background-color: var(--chakra-colors-background-default);
  cursor: pointer;
  transition: background-color 0.1s ease-in-out;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: var(--chakra-colors-background-lessSubtle);
  }

  & ${StyledAddIcon} {
    transition: transform 0.1s ease-in-out;
    width: ${({ size }): string => (size === 'sm' ? '10px' : '12px')};
    height: ${({ size }): string => (size === 'sm' ? '10px' : '12px')};
  }

  &:hover ${StyledAddIcon} {
    transform: scale(1.2);
  }
`;

const AvatarCount = styled.div<IAddAvatarButtonProps>`
  height: ${({ size }): string => getAvatarSize(size)};
  width: ${({ size }): string => getAvatarSize(size)};
  color: var(--chakra-colors-text-onContrast);
  border-radius: 50px;
  background-color: var(--chakra-colors-background-moreSubtleContrast);
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: ${({ size }): string => getAvatarFontSize(size)};;
  cursor: pointer;
`;

const getAvatarSize = (size: AvatarSize | undefined): string => {
  return size === 'sm' ? '30px' : size === 'xs' ? '24px' : '36px';
}

const getAvatarFontSize = (size: AvatarSize | undefined): string => {
  return size === 'sm' ? '17px' : size === 'xs' ? '12px' : '17px';
}
